import { useEffect } from "react";
import { Capacitor, registerPlugin } from "@capacitor/core";

interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface SafeAreaPlugin {
  getInsets(): Promise<SafeAreaInsets>;
}

const NativeSafeArea = registerPlugin<SafeAreaPlugin>("SafeArea");

/**
 * Hook that handles safe area insets for different platforms
 * Sets CSS custom properties for safe area values that can be used in stylesheets
 */
export const useSafeArea = () => {
  useEffect(() => {
    const setPxVar = (name: string, val: number) =>
      document.documentElement.style.setProperty(name, `${Math.round(val)}px`);

    // Add minimal fallback for development mode or non-Android
    const setDefaultInsets = () => {
      setPxVar("--safe-area-inset-top", 0); // Status bar height
      setPxVar("--safe-area-inset-bottom", 0);
      setPxVar("--safe-area-inset-left", 0);
      setPxVar("--safe-area-inset-right", 0);
    };

    const freezeInsets = async () => {
      let top = 0,
        bottom = 0,
        left = 0,
        right = 0;
      if (Capacitor.getPlatform() === "android") {
        try {
          const { top: t, bottom: b, left: l, right: r } = await NativeSafeArea.getInsets();
          top = t;
          bottom = b;
          left = l;
          right = r;
        } catch (e) {
          // Fallback if getInsets fails
          setDefaultInsets();
          return;
        }
      } else {
        // For iOS/web, read env() values via a helper element
        // (env() values can't be read directly from custom properties)
        const readEnvValue = (
          envName: string,
          cssProp: "paddingTop" | "paddingBottom" | "paddingLeft" | "paddingRight"
        ) => {
          const el = document.createElement("div");
          el.style.position = "fixed";
          el.style.visibility = "hidden";
          el.style[cssProp] = `env(${envName}, 0px)`;
          document.body.appendChild(el);
          const val = parseFloat(getComputedStyle(el)[cssProp]);
          document.body.removeChild(el);
          return Number.isFinite(val) ? val : 0;
        };
        top = readEnvValue("safe-area-inset-top", "paddingTop");
        bottom = readEnvValue("safe-area-inset-bottom", "paddingBottom");
        left = readEnvValue("safe-area-inset-left", "paddingLeft");
        right = readEnvValue("safe-area-inset-right", "paddingRight");
      }

      if (Capacitor.getPlatform() === "android") {
        setPxVar("--safe-area-top-fixed", top);
        setPxVar("--safe-area-bottom-fixed", bottom);
        setPxVar("--safe-area-left-fixed", left);
        setPxVar("--safe-area-right-fixed", right);
        return;
      }

      // Only set CSS vars if we got a real value — avoids overriding the
      // env() CSS fallback with 0 before the WebView reports true insets.
      if (top > 0 || bottom > 0 || left > 0 || right > 0) {
        setPxVar("--safe-area-top-fixed", top);
        setPxVar("--safe-area-bottom-fixed", bottom);
        setPxVar("--safe-area-left-fixed", left);
        setPxVar("--safe-area-right-fixed", right);
      }
    };

    const run = async () => {
      if (Capacitor.getPlatform() !== "web") {
        try {
          if (Capacitor.getPlatform() === "ios") {
            freezeInsets();
          }
          // Re-apply on app resume (for dynamic insets like keyboard)
          (window as any).__hp_sa_resume_sub__ = import("@capacitor/app").then(({ App }) =>
            App.addListener("resume", freezeInsets)
          );
        } catch (error) {
          if (Capacitor.isNativePlatform() && process.env.REACT_APP_CAPACITOR_MODE === "dev") {
            setDefaultInsets();
          }
        }
      }
      requestAnimationFrame(() => {
        if (Capacitor.getPlatform() === "web") return;
        freezeInsets();
        const topFixed = parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue("--safe-area-top-fixed")
        );
        // Retry with increasing delays until env() values become available
        if (!topFixed || topFixed === 0) {
          setTimeout(freezeInsets, 60);
          setTimeout(freezeInsets, 200);
          setTimeout(freezeInsets, 500);
          setTimeout(freezeInsets, 1000);
          setTimeout(freezeInsets, 2000);
        }
      });
    };

    run();

    const onRotate = () => Capacitor.getPlatform() !== "web" && freezeInsets();
    const onShow = () => Capacitor.getPlatform() !== "web" && freezeInsets();
    window.addEventListener("orientationchange", onRotate);
    document.addEventListener("visibilitychange", onShow);

    return () => {
      window.removeEventListener("orientationchange", onRotate);
      document.removeEventListener("visibilitychange", onShow);
      const subP = (window as any).__hp_sa_resume_sub__;
      if (subP && typeof subP.then === "function") {
        subP.then((sub: { remove: () => void }) => sub?.remove?.()).catch(() => {});
      }
    };
  }, []);
};
